import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {EMPTY, Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, skip, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';

import {EventType, ItemType, Level, LogEvent, Status, UpdateLogEvent} from './log-event.model';
import {LogEventService} from './log-event.service';

import {isNullOrUndefined} from 'util';
import {Destroyable} from '../../destroyable.component';
import {RolesService} from '../authentication/roles/roles.service';
import {ModalService} from '../modal';
import {DefaultPagination, Pagination} from '../ui';
import {SortDirection} from '../ui/table/table.constants';
import {SortKey} from '../ui/table/table.interfaces';
import {User, UserService} from '../user';
import {extractValue} from '../utils/browser';
import {Enums} from '../utils/enums';

const areStatusesEqual = (status1: string, status2: string): boolean => {
  if (isNullOrUndefined(status1)) {
    status1 = null;
  }

  if (isNullOrUndefined(status2)) {
    status2 = null;
  }

  return status1 === status2;
};


@Component({
  selector: 'gp-log-event',
  templateUrl: './log-event.component.html',
  styleUrls: ['./log-event.component.scss']
})
export class LogEventComponent extends Destroyable implements OnInit {

  @Input() public tableOnly = false;

  public loading: boolean = true;

  public events: LogEvent[] = [];

  public pagination: Pagination = new DefaultPagination(10);

  public sortKey: SortKey = [{key: 'createDate', direction: SortDirection.DESCENDING}];

  public levels = Enums.getNames(Level);
  public eventTypes = Enums.getNames(EventType);
  public statuses = Enums.getNames(Status);

  public errorLevel: Level = Level.ERROR;
  public technicalType: EventType = EventType.TECHNICAL;

  public filtersForm: UntypedFormGroup;

  public logEventModificationForm: UntypedFormGroup;

  public extractValue = extractValue;

  private itemId: number;
  private itemType: string;
  private hiddenCategory: string;

  private selectedLogEvent: LogEvent;
  public selectedStatus: Status | null;
  public hoverByIndex: boolean[] = [];
  public timezone: string;
  public isEventLogStatusReadOnly;

  constructor(
    private logEventService: LogEventService,
    private modalService: ModalService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private cd: ChangeDetectorRef,
    private rolesService: RolesService) {
    super();
    this.filtersForm = this.formBuilder.group({
      createDate: ['', ''],
      reference: ['', ''],
      level: ['', ''],
      eventType: ['', ''],
      category: ['', ''],
      message: ['', '']
    });
    this.logEventModificationForm = this.formBuilder.group({
      status: ['', ''],
      comment: ['', '']
    });
  }

  ngOnInit() {
    this.isEventLogStatusReadOnly = !this.rolesService.hasRole('EVENT_LOG_STATUS_WRITE');
    this.filtersForm.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(400),
        startWith(this.filtersForm.value as object),
        distinctUntilChanged(isEqual),
        skip(1),
        switchMap(() => this.fetchEvents())
      )
      .subscribe();
    this.pagination.pageChanged
      .asObservable()
      .pipe(
        switchMap(() => this.fetchEvents()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.userService.getUser()
      .pipe(
        tap((user: User) => this.timezone = user.timezone),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public refresh(): void {
    this.logEventService.logEventsRefreshed$.next();
    this.fetchEvents().subscribe();
  }

  public load(itemId: number, itemType: ItemType, hiddenCategory = null) {
    if (!this.itemId || this.itemId !== itemId) {
      this.itemId = itemId;
      this.itemType = ItemType[itemType];
      this.hiddenCategory = hiddenCategory;
      this.fetchEvents().subscribe();
    }
  }

  public onSortKeyChange(key: SortKey): void {
    this.sortKey = key;
    this.fetchEvents().subscribe();
  }

  private fetchEvents(): Observable<any> {
    if (!this.itemId || !this.itemType) {
      return EMPTY;
    }
    this.loading = true;
    const orderBy = this.sortKey.map(element => {
      const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
      return `${prefix}${element.key}`;
    }).join(',');

    return this.logEventService.findLogEvents(this.itemId,
      this.itemType, this.filtersForm.get('level').value,
      this.filtersForm.get('eventType').value, this.filtersForm.get('category').value,
      this.filtersForm.get('createDate').value, this.filtersForm.get('message').value,
      this.hiddenCategory,
      this.pagination.perPage, this.pagination.currentPage.number, orderBy)
      .pipe(
        tap((result: { total: number, data: LogEvent[] }) => {
          this.events = result.data;
          this.pagination.update(result.total);
        }),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      );
  }

  public onChangeStatus(log: LogEvent, status: Status | null): void {
    if (!areStatusesEqual(log.status, status)) {
      this.selectedStatus = status;
      this.selectedLogEvent = log;
      this.modalService.openModalWithId('logEventModification');
    }
  }

  public transformStatus(status: any): Status | null {
    return status !== 'null' ? status : null;
  }

  public updateLogEvent(): void {
    const updateLogEvent: UpdateLogEvent = {
      status: this.selectedStatus,
      comment: this.logEventModificationForm.get('comment').value
    };
    this.modalService.modalClosed.emit('logEventModification');
    this.logEventService.patchLogEvent(this.selectedLogEvent.id, updateLogEvent)
      .pipe(
        tap(() => this.logEventModificationForm.get('comment').setValue('')),
        tap(() => this.logEventService.logEventStatusModified.next(true))
      ).subscribe();
  }

  resetFilters(): void {
    if (this.isFiltering()) {
      this.filtersForm.reset();
      this.fetchEvents().subscribe();
    }
  }

  public isFiltering(): boolean {
    return !isEmpty(this.filtersForm.get('createDate').value) ||
      !isEmpty(this.filtersForm.get('level').value) ||
      !isEmpty(this.filtersForm.get('eventType').value) ||
      !isEmpty(this.filtersForm.get('category').value) ||
      !isEmpty(this.filtersForm.get('message').value);
  }
}
